
import { useState, useRef, useEffect } from 'react';
import { toast } from '@/components/ui/sonner';
import { SpeechSynthesisOptions, UseSpeechSynthesisReturn } from './types';
import { useVoiceOptions } from './useVoiceOptions';
import { useSpeechProgress } from './useSpeechProgress';
import { 
  cleanTextForSpeech, 
  breakTextIntoChunks, 
  isSpeechSynthesisSupported,
  setupKeepAlive
} from './speechUtils';

export const useSpeechSynthesis = (): UseSpeechSynthesisReturn => {
  const {
    availableVoices,
    selectedVoice,
    setSelectedVoice,
    rate,
    setRate,
    pitch, 
    setPitch,
    volume,
    setVolume,
  } = useVoiceOptions();
  
  const {
    isPlaying,
    setIsPlaying,
    isPaused,
    setIsPaused,
    progress,
    setProgress,
    isLoading,
    setIsLoading,
    hasError,
    setHasError,
    progressIntervalRef,
    textLengthRef,
    startTimeRef,
    speechCheckTimeoutRef,
    startProgressTracking,
    clearProgressTracking,
  } = useSpeechProgress();

  const speechSynthRef = useRef<SpeechSynthesis | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const hasTriedFallbackRef = useRef<boolean>(false);
  
  // Check if speech synthesis is supported
  const isSpeechSupported = isSpeechSynthesisSupported();

  // Initialize Speech Synthesis
  useEffect(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      speechSynthRef.current = window.speechSynthesis;
      
      const cleanupKeepAlive = setupKeepAlive(speechSynthRef, isPlaying, isPaused);
      
      return () => {
        cleanupKeepAlive();
        if (speechSynthRef.current && isPlaying) {
          speechSynthRef.current.cancel();
        }
        
        clearProgressTracking();
      };
    }
  }, [isPlaying, isPaused]);

  // Track progress during speech
  useEffect(() => {
    if (isPlaying && !isPaused && speechSynthRef.current) {
      const cleanup = startProgressTracking(rate, speechSynthRef);
      
      // Safety check to ensure speech is actually playing
      speechCheckTimeoutRef.current = window.setTimeout(() => {
        if (isPlaying && speechSynthRef.current && !speechSynthRef.current.speaking && !hasTriedFallbackRef.current) {
          console.log("Speech synthesis might be stuck - attempting to restart");
          const currentUtterance = utteranceRef.current;
          if (currentUtterance) {
            speechSynthRef.current.cancel();
            speechSynthRef.current.speak(currentUtterance);
          }
        }
      }, 2000);
      
      return cleanup;
    }
  }, [isPlaying, isPaused, rate]);

  const generateSpeech = ({ text, onStart, onEnd, onError }: SpeechSynthesisOptions) => {
    if (!isSpeechSupported && !hasTriedFallbackRef.current) {
      toast.error("Your browser doesn't support speech synthesis");
      setHasError(true);
      if (onError) onError(new Error("Speech synthesis not supported"));
      return;
    }
    
    try {
      setIsLoading(true);
      setHasError(false);
      
      // Cancel any ongoing speech
      if (speechSynthRef.current) {
        speechSynthRef.current.cancel();
      }
      
      // Break very long text into smaller chunks to avoid browser limits
      const chunks = breakTextIntoChunks(text);
      
      // Create one or more utterances
      const utterances = chunks.map((chunk, index) => {
        const utterance = new SpeechSynthesisUtterance(chunk);
        
        // Set voice
        const voice = availableVoices.find(v => v.name === selectedVoice);
        if (voice) {
          utterance.voice = voice;
        }
        
        // Set other properties
        utterance.volume = volume;
        utterance.rate = rate;
        utterance.pitch = pitch;
        
        // Only attach events to the first utterance
        if (index === 0) {
          utterance.onstart = () => {
            setIsPlaying(true);
            setIsPaused(false);
            startTimeRef.current = Date.now();
            if (onStart) onStart();
          };
        }
        
        // Only attach end event to the last utterance
        if (index === chunks.length - 1) {
          utterance.onend = () => {
            setIsPlaying(false);
            setProgress(100);
            clearProgressTracking();
            if (onEnd) onEnd();
          };
        }
        
        utterance.onerror = (event) => {
          console.error('SpeechSynthesis error:', event);
          setHasError(true);
          
          if (!hasTriedFallbackRef.current) {
            hasTriedFallbackRef.current = true;
            toast.error('Speech synthesis failed. Trying fallback...');
            tryFallbackSpeech(text, onStart, onEnd, onError);
          } else {
            toast.error('Speech synthesis failed. Please try again with different settings.');
            setIsPlaying(false);
            clearProgressTracking();
            if (onError) onError(event);
          }
        };
        
        return utterance;
      });
      
      // Store reference to the current utterance
      if (utterances.length > 0) {
        utteranceRef.current = utterances[0];
        textLengthRef.current = text.length;
      }
      
      // Start speaking
      if (speechSynthRef.current) {
        // Queue all utterances
        utterances.forEach(utterance => {
          speechSynthRef.current?.speak(utterance);
        });
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error generating speech:', error);
      setHasError(true);
      
      if (!hasTriedFallbackRef.current) {
        hasTriedFallbackRef.current = true;
        toast.error('Error in speech synthesis. Trying fallback...');
        tryFallbackSpeech(text, onStart, onEnd, onError);
      } else {
        toast.error('Failed to generate audio. Please try again with different settings.');
        setIsLoading(false);
        if (onError) onError(error);
      }
    }
  };

  // Attempt fallback with simpler configuration
  const tryFallbackSpeech = (text: string, onStart?: () => void, onEnd?: () => void, onError?: (error: any) => void) => {
    try {
      if (!speechSynthRef.current) return;
      
      speechSynthRef.current.cancel();
      
      const simpleUtterance = new SpeechSynthesisUtterance(text);
      // Use default voice and settings
      simpleUtterance.rate = 1;
      simpleUtterance.pitch = 1;
      simpleUtterance.volume = 1;
      
      simpleUtterance.onstart = () => {
        setIsPlaying(true);
        setIsPaused(false);
        startTimeRef.current = Date.now();
        if (onStart) onStart();
      };
      
      simpleUtterance.onend = () => {
        setIsPlaying(false);
        setProgress(100);
        clearProgressTracking();
        if (onEnd) onEnd();
      };
      
      simpleUtterance.onerror = (event) => {
        console.error('Fallback speech synthesis error:', event);
        toast.error('Speech synthesis failed. Please try a different browser.');
        setIsPlaying(false);
        clearProgressTracking();
        if (onError) onError(event);
      };
      
      utteranceRef.current = simpleUtterance;
      textLengthRef.current = text.length;
      
      speechSynthRef.current.speak(simpleUtterance);
      setIsLoading(false);
    } catch (error) {
      console.error('Error in fallback speech:', error);
      toast.error('Speech synthesis is not available in your browser.');
      setIsLoading(false);
      if (onError) onError(error);
    }
  };

  const togglePlayPause = () => {
    if (isLoading) return;
    
    if (!speechSynthRef.current) {
      toast.error("Your browser doesn't support speech synthesis");
      return;
    }
    
    if (isPlaying) {
      if (isPaused) {
        // Resume speech
        speechSynthRef.current.resume();
        setIsPaused(false);
      } else {
        // Pause speech
        speechSynthRef.current.pause();
        setIsPaused(true);
      }
    }
  };

  const stopSpeech = () => {
    if (speechSynthRef.current) {
      speechSynthRef.current.cancel();
      setIsPlaying(false);
      setIsPaused(false);
      setProgress(0);
    }
  };

  const updateVolume = (newVolume: number) => {
    setVolume(newVolume);
    
    // Update current utterance if speaking
    if (utteranceRef.current) {
      utteranceRef.current.volume = newVolume;
    }
  };

  return {
    isPlaying,
    isPaused,
    progress,
    volume,
    rate,
    pitch,
    isLoading,
    hasError,
    availableVoices,
    selectedVoice,
    isSpeechSupported,
    generateSpeech,
    togglePlayPause,
    stopSpeech,
    updateVolume,
    setRate,
    setPitch,
    setSelectedVoice,
  };
};

export default useSpeechSynthesis;
